import "keen-slider/keen-slider.min.css";
import React from "react";
import { CasestudyCard, casestudyCards } from ".";
import NewLayout from "@components/new-ui/Layout";
import { CaseStudyHerosection, CaseStudyInformation, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";

const ShuraaTaxAccountingAuditing: React.FC = () => {
    return (
        <NewLayout
            title="Shuraa Tax Enhances Client Engagement with WhatsApp Automation"
            description="See how Shuraa Tax simplified client communication, automated support, and increased employee productivity using Gallabox's automation platform."
            canonical="https://gallabox.com/case-study/shuraa-tax-accounting-auditing"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/shuraa-tax-og.png" }}
        >
            <CaseStudyHerosection
                title="How Shuraa Tax Accounting & Auditing enhanced communication with Gallabox"
                description="See how Shuraa Tax simplified client communication, automated support, and increased employee productivity using Gallabox's automation platform."
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/shuraa-tax.png"}
                redirectUrl={"/case-study"}
            />

            <StatsInfo
                statsInfo={[
                    { percentage: "86%", label: "Overall improvement in business growth" },
                    { percentage: "50%", label: "Increase in customer satisfaction" },
                    { percentage: "40%", label: "Expansion in customer base" },
                ]}
            />
            <section className="max-w-6xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[
                                { label: "Company", value: ["Shuraa Tax Accounting & Auditing"] },
                                { label: "Industry", value: ["Service"] },
                            ]}
                            product={{
                                title: "Features used",
                                productLink: [
                                    { link: "https://gallabox.com/whatsapp-chatbot", label: "Chatbots" },
                                    { link: "https://gallabox.com/whatsapp-integrations", label: "Integrations" },
                                ],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12">
                        <section id="About">
                            <p>
                                Shuraa Tax Accounting & Auditing is a professional services firm offering tax consultancy, compliance, and auditing solutions. With a client-focused
                                approach, Shuraa needed a better way to manage communication and deliver timely support to its growing base of customers.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation
                                subTitle={"Challenges before Gallabox"}
                                descriptions={[
                                    "Before Gallabox, Shuraa relied on traditional, manual communication methods. While the process was manageable, it had significant drawbacks:",
                                ]}
                                className="mt-0"
                            />
                            <FeatureList
                                List={[
                                    { listItem: "Lack of automation: Every client interaction required manual effort, leaving the team stretched." },
                                    { listItem: "Delays in response: Without automated systems, some queries were not addressed promptly." },
                                    { listItem: "Difficulty scaling operations: As the client base grew, maintaining personalized and consistent communication became harder." },
                                ]}
                                className="mt-5 bg-gray-100"
                            />
                            <CaseStudyInformation
                                subTitle={"Initial solutions tried"}
                                descriptions={[
                                    "The firm primarily used manual email and phone communication, which were easy but did not offer the structure, tracking, or automation needed to improve efficiency and scale effectively.",
                                ]}
                                className="mt-12"
                            />
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation
                                subTitle={"Why Shuraa chose Gallabox"}
                                descriptions={[
                                    "Gallabox was chosen because it had already been tested and proven in similar business environments. The firm valued Gallabox's reliability and ease of use, which meant it could be quickly adopted by the team without heavy training. Gallabox was seen as a platform that could grow with the business while addressing its immediate communication challenges.",
                                    "By adopting Gallabox, Shuraa aimed to:",
                                ]}
                                className="mt-0"
                            />
                            <FeatureList
                                List={[
                                    { listItem: "Streamline customer communication by centralizing all conversations." },
                                    { listItem: "Automate client support to ensure faster, more consistent responses." },
                                    { listItem: "Retain existing clients by engaging them proactively." },
                                    { listItem: "Increase employee productivity by reducing manual tasks." },
                                ]}
                                className="mt-5 bg-gray-100"
                            />
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation
                                subTitle={"Impact on business growth"}
                                descriptions={["Since adopting Gallabox, Shuraa has seen impressive improvements:"]}
                                className="mt-0"
                            />
                            <FeatureList
                                List={[
                                    { listItem: "An 86% overall improvement in business growth." },
                                    { listItem: "40% increase in employee productivity, as routine work was automated." },
                                    { listItem: "50% rise in customer satisfaction, with quicker and more reliable support." },
                                    { listItem: "40% expansion in customer base, thanks to better engagement." },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <CaseStudyInformation
                                subTitle={"Simplifying client journeys for lasting trust"}
                                descriptions={[
                                    "For Shuraa Tax Accounting & Auditing, Gallabox has become an essential tool in addressing communication bottlenecks and automating repetitive support tasks. The platform's ease of use, combined with reliable chatbot and integration features, has streamlined client interactions while freeing up valuable team bandwidth.",
                                    "The improvements in employee productivity, client base expansion, and operational efficiency clearly reflect the role Gallabox plays in enhancing Shuraa's growth trajectory. As the firm continues to scale, Gallabox remains central to its digital communication strategy.",
                                ]}
                                className="mt-10"
                            />
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <section className="max-w-6xl mx-auto">
                <h3 className="text-gray-900 mb-10 mt-5 text-left ml-4">Check out other customer stories</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto mb-20">
                    {casestudyCards.slice(0, 3)?.map((data, index) => (
                        <CasestudyCard key={index} {...data} />
                    ))}
                </div>
            </section>
        </NewLayout>
    );
};

export default ShuraaTaxAccountingAuditing;
